import {
  AlertTriangle, BarChart3, BookOpen, Bot, Brain, Briefcase, Building2, Compass,
  CreditCard, DollarSign, Factory, FileText, FlaskConical, Globe, GraduationCap,
  Handshake, Landmark, Laptop, Leaf, Lightbulb, Lock, Mailbox, MapPin, Microscope,
  Mountain, Puzzle, Rocket, Scale, ScrollText, Search, Shield, Snowflake, Sparkles,
  Stethoscope, Tag, TrendingUp, User, Users, Waves, Wrench,
} from 'lucide-react';
import React, { useState, useEffect, useMemo, ReactNode, useCallback } from 'react';
import { getMasterData, type MasterData } from '../../api/masterDataApi';
import { getJurisdictions } from '../../api/dashboardApi';
import { useNavigate } from 'react-router-dom';
import Select, { StylesConfig } from 'react-select';
import { Country, State } from 'country-state-city';
import postalCodes from 'postal-codes-js';

import { useAuth } from '../../context/AuthContext';
import { useBilling } from '../../context/BillingContext';
import { completeOnboarding } from '../../api/authApi';
import ErrorBanner from '../../components/ErrorBanner';
import { Eyebrow } from '../../design-system';
import styles from './OnboardingPage.module.css';

interface OnboardingFormData {
  country: string;
  state: string;
  postal_code: string;
  organization_name: string;
  industry: string;
  organization_type: string;
  job_role: string;
  primary_jurisdictions: string[];
  regulation_types: string[];
  use_cases: string[];
  team_size: string;
  custom_fields: Record<string, string>;
}

type SelectOption = {
  value: string;
  label: string;
};

type CountryOption = SelectOption & {
  isoCode: string;
  region: string;
};

type StateOption = SelectOption & {
  stateCode: string;
  countryCode: string;
};

type QuestionIcon = 'globe' | 'map' | 'pin' | 'mail' | 'building' | 'briefcase' | 'checkbox' | 'spark' | 'team' | 'chart';

type QuestionConfig = {
  id: string;
  title: string;
  subtitle: string;
  icon: QuestionIcon;
  optional?: boolean;
  validate: () => boolean;
  errorMessage?: string;
  renderBody: () => ReactNode;
};

const CONTINENT_ICONS: Record<string, ReactNode> = {
  Africa: <Globe size={20} aria-hidden />,
  Antarctica: <Snowflake size={20} aria-hidden />,
  Asia: <Globe size={20} aria-hidden />,
  Europe: '🇪🇺',
  Oceania: <Waves size={20} aria-hidden />,
  'North America': <Landmark size={20} aria-hidden />,
  'South America': <Mountain size={20} aria-hidden />,
  Americas: <Globe size={20} aria-hidden />,
  Other: <Globe size={20} aria-hidden />,
};

const INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Finance',
  'Manufacturing',
  'Legal Services',
  'Government',
  'Education',
  'Consulting',
  'Other',
];

const INDUSTRY_ICONS: Record<string, ReactNode> = {
  Technology: <Laptop size={20} aria-hidden />,
  Healthcare: <Stethoscope size={20} aria-hidden />,
  Finance: <DollarSign size={20} aria-hidden />,
  Manufacturing: <Factory size={20} aria-hidden />,
  'Legal Services': <Scale size={20} aria-hidden />,
  Government: <Landmark size={20} aria-hidden />,
  Education: <BookOpen size={20} aria-hidden />,
  Consulting: <Brain size={20} aria-hidden />,
  Other: <Sparkles size={20} aria-hidden />,
};

const ORGANIZATION_TYPES = [
  'Corporation',
  'Startup',
  'Government Agency',
  'Non-Profit',
  'Law Firm',
  'Consulting Firm',
  'Academic Institution',
  'Individual Researcher',
];

const ORGANIZATION_TYPE_ICONS: Record<string, ReactNode> = {
  Corporation: <Building2 size={20} aria-hidden />,
  Startup: <Rocket size={20} aria-hidden />,
  'Government Agency': <Shield size={20} aria-hidden />,
  'Non-Profit': <Handshake size={20} aria-hidden />,
  'Law Firm': <Scale size={20} aria-hidden />,
  'Consulting Firm': <Brain size={20} aria-hidden />,
  'Academic Institution': <GraduationCap size={20} aria-hidden />,
  'Individual Researcher': <FlaskConical size={20} aria-hidden />,
};

const JOB_ROLES = [
  'Compliance Officer',
  'Legal Counsel',
  'Policy Analyst',
  'Executive/C-Suite',
  'Product Manager',
  'Engineer',
  'Researcher',
  'Consultant',
  'Other',
];

const JOB_ROLE_ICONS: Record<string, ReactNode> = {
  'Compliance Officer': <Shield size={20} aria-hidden />,
  'Legal Counsel': <ScrollText size={20} aria-hidden />,
  'Policy Analyst': <BarChart3 size={20} aria-hidden />,
  'Executive/C-Suite': <Briefcase size={20} aria-hidden />,
  'Product Manager': <Puzzle size={20} aria-hidden />,
  Engineer: <Wrench size={20} aria-hidden />,
  Researcher: <Microscope size={20} aria-hidden />,
  Consultant: <Briefcase size={20} aria-hidden />,
  Other: <Sparkles size={20} aria-hidden />,
};

/**
 * Only a fallback, for when the corpus cannot be reached.
 *
 * These names have to be the ones the corpus stores, because entitlement
 * matches on the name: "United States Federal" matched nothing at all, since
 * the corpus holds "United States" (code US-FED) — so the single most likely
 * choice silently filtered the product down to nothing. "Australia" is in the
 * same position: the corpus has no Australian content, so choosing it emptied
 * the account. The real list now comes from the corpus itself.
 */
export const JURISDICTIONS_FALLBACK = [
  'United States',
  'European Union',
  'United Kingdom',
  'China',
  'India',
  'Canada',
  'Japan',
  'Singapore',
  'Other',
];


const REGULATION_TYPES = [
  'AI/ML Regulations',
  'Data Privacy (GDPR, CCPA, etc.)',
  'Financial Compliance',
  'Healthcare Regulations',
  'Environmental Regulations',
  'Consumer Protection',
  'Other',
];

const REGULATION_TYPE_ICONS: Record<string, ReactNode> = {
  'AI/ML Regulations': <Bot size={20} aria-hidden />,
  'Data Privacy (GDPR, CCPA, etc.)': <Lock size={20} aria-hidden />,
  'Financial Compliance': <CreditCard size={20} aria-hidden />,
  'Healthcare Regulations': <Stethoscope size={20} aria-hidden />,
  'Environmental Regulations': <Leaf size={20} aria-hidden />,
  'Consumer Protection': <Shield size={20} aria-hidden />,
  Other: <BookOpen size={20} aria-hidden />,
};

const USE_CASES = [
  'Monitor regulatory changes',
  'Research compliance requirements',
  'Policy analysis and research',
  'Risk assessment',
  'Educational purposes',
  'Other',
];

const USE_CASE_ICONS: Record<string, ReactNode> = {
  'Monitor regulatory changes': <TrendingUp size={20} aria-hidden />,
  'Research compliance requirements': <Search size={20} aria-hidden />,
  'Policy analysis and research': <FileText size={20} aria-hidden />,
  'Risk assessment': <AlertTriangle size={20} aria-hidden />,
  'Educational purposes': <GraduationCap size={20} aria-hidden />,
  Other: <Lightbulb size={20} aria-hidden />,
};

const TEAM_SIZES = [
  'Solo user',
  '2-10 people',
  '11-50 people',
  '51-200 people',
  '200+ people',
];

const TEAM_SIZE_ICONS: Record<string, ReactNode> = {
  'Solo user': <User size={20} aria-hidden />,
  '2-10 people': <Users size={20} aria-hidden />,
  '11-50 people': <Users size={20} aria-hidden />,
  '51-200 people': <Building2 size={20} aria-hidden />,
  '200+ people': <Globe size={20} aria-hidden />,
};

const getQuestionIconSvg = (type: QuestionIcon) => {
  switch (type) {
    case 'globe':
      return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18" />
          <path d="M12 3a15 15 0 0 1 0 18" />
          <path d="M12 3a15 15 0 0 0 0 18" />
        </svg>
      );
    case 'map':
      return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2z" />
          <path d="M9 4v16" />
          <path d="M15 6v16" />
        </svg>
      );
    case 'pin':
      return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 21s-6-5.2-6-10a6 6 0 1 1 12 0c0 4.8-6 10-6 10z" />
          <circle cx="12" cy="11" r="2" />
        </svg>
      );
    case 'mail':
      return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="5" width="18" height="14" rx="3" />
          <path d="M3 8l9 6 9-6" />
        </svg>
      );
    case 'building':
      return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 20V7l8-4 8 4v13" />
          <path d="M9 20v-4h2v4" />
          <path d="M13 20v-4h2v4" />
          <path d="M4 20h16" />
        </svg>
      );
    case 'briefcase':
      return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 7h16v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7z" />
          <path d="M10 7V5a2 2 0 0 1 2-2h0a2 2 0 0 1 2 2v2" />
          <path d="M4 12h16" />
        </svg>
      );
    case 'checkbox':
      return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="3" />
          <path d="M8 12l3 3 5-6" />
        </svg>
      );
    case 'spark':
      return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2v4" />
          <path d="M12 18v4" />
          <path d="M4.93 4.93l2.83 2.83" />
          <path d="M16.24 16.24l2.83 2.83" />
          <path d="M2 12h4" />
          <path d="M18 12h4" />
          <path d="M4.93 19.07l2.83-2.83" />
          <path d="M16.24 7.76l2.83-2.83" />
        </svg>
      );
    case 'team':
      return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="8" cy="8" r="3" />
          <circle cx="16" cy="8" r="3" />
          <path d="M3 21v-2a4 4 0 0 1 4-4h0" />
          <path d="M17 15a4 4 0 0 1 4 4v2" />
          <path d="M12 12a4 4 0 0 1 4 4v5" />
        </svg>
      );
    case 'chart':
    default:
      return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 20V10" />
          <path d="M12 20V4" />
          <path d="M20 20v-7" />
          <path d="M3 20h18" />
        </svg>
      );
  }
};

type QuestionCardProps = {
  title: string;
  subtitle: string;
  icon: QuestionIcon;
  children: ReactNode;
  introActive?: boolean;
};

type HelperPanelProps = {
  icon: ReactNode;
  title: string;
  bullets: string[];
};

const QuestionCard: React.FC<QuestionCardProps> = ({ title, subtitle, icon, children, introActive = false }) => (
  <div className={`${styles.questionCard} ${styles.cardIntro} ${introActive ? styles.cardIntroActive : ''}`}>
    <div className={`${styles.questionHeader} ${styles.questionIntro} ${introActive ? styles.questionIntroActive : ''}`}>
      <span className={`${styles.questionIcon} ${styles[`questionIcon-${icon}`]}`}>
        {getQuestionIconSvg(icon)}
      </span>
      <div>
        <p className={styles.questionTitle}>{title}</p>
        <p className={styles.questionSubtitle}>{subtitle}</p>
      </div>
    </div>
    <div className={`${styles.questionBody} ${styles.optionsIntro} ${introActive ? styles.optionsIntroActive : ''}`}>
      {children}
    </div>
  </div>
);

const HelperPanel: React.FC<HelperPanelProps> = ({ icon, title, bullets }) => (
  <div className={styles.helperPanel}>
    <div className={styles.helperIcon}>{icon}</div>
    <div className={styles.helperBody}>
      <p className={styles.helperTitle}>{title}</p>
      <ul className={styles.helperList}>
        {bullets.map(point => (
          <li key={point}>{point}</li>
        ))}
      </ul>
    </div>
  </div>
);

/**
 * react-select cannot read CSS custom properties, so the HireAI design-system
 * token values are inlined here as their canonical hex. Keep in sync with
 * /design-system/tokens.css:
 *   DS_PRIMARY  var(--ds-brand-blue)  → --primary-600 / --ds-info  (selected + focused border)
 *   DS_BORDER   var(--ds-border)  → --gray-200 / --ds-border
 *   DS_INFO_BG  rgba(68,76,231,0.08) → --ds-info-bg   (focused option row)
 *   DS_TEXT     var(--ds-text)  → --gray-900 / --ds-text
 *   DS_FAINT    var(--ds-text-faint)  → --gray-400 / --ds-text-faint (placeholder)
 *   DS_RADIUS_LG 12px    → --ds-radius-lg (input control)
 *   DS_RADIUS_XL 16px    → --ds-radius-xl (menu)
 */
const DS_PRIMARY = 'var(--ds-brand-blue)';
const DS_BORDER = 'var(--ds-border)';
const DS_INFO_BG = 'var(--ds-surface-2)';
const DS_TEXT = 'var(--ds-text)';
const DS_FAINT = 'var(--ds-text-faint)';

// The same look, for the multi-select the jurisdiction step uses.
const multiSelectStyles: StylesConfig<SelectOption, true> = {
  control: (base, state) => ({
    ...base,
    borderRadius: 12,
    borderColor: state.isFocused ? DS_PRIMARY : DS_BORDER,
    boxShadow: state.isFocused ? '0 0 0 4px rgba(68, 76, 231, 0.12)' : 'none',
    minHeight: 48,
    background: 'var(--ds-surface)',
    ':hover': { borderColor: DS_PRIMARY },
  }),
  menu: (base) => ({ ...base, borderRadius: 16, background: 'var(--ds-surface)', zIndex: 20 }),
  option: (base, state) => ({
    ...base,
    background: state.isFocused ? 'var(--ds-surface-2)' : 'transparent',
    color: DS_TEXT,
    cursor: 'pointer',
  }),
  multiValue: (base) => ({ ...base, background: 'var(--ds-surface-2)', borderRadius: 999 }),
  multiValueLabel: (base) => ({ ...base, color: DS_TEXT }),
  multiValueRemove: (base) => ({ ...base, color: DS_FAINT, ':hover': { color: DS_TEXT } }),
  input: (base) => ({ ...base, color: DS_TEXT }),
  placeholder: (base) => ({ ...base, color: DS_FAINT }),
};

const selectStyles: StylesConfig<SelectOption, false> = {
  control: (base, state) => ({
    ...base,
    borderRadius: 12, /* --ds-radius-lg */
    borderColor: state.isFocused ? DS_PRIMARY : DS_BORDER,
    boxShadow: state.isFocused ? '0 0 0 4px rgba(68, 76, 231, 0.12)' /* --ds-shadow-blue */ : 'none',
    minHeight: 48,
    cursor: 'pointer',
    // react-select defaults every surface to white. The multi-select below set
    // this; this one never did, so in the dark theme the closed control was a
    // white box and the open menu was white with near-white option text — the
    // country names were invisible, every one of them except the highlighted
    // row, which carries its own background.
    background: 'var(--ds-surface)',
    ':hover': {
      borderColor: DS_PRIMARY,
    },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 16, /* --ds-radius-xl */
    background: 'var(--ds-surface)',
    border: `1px solid ${DS_BORDER}`,
    boxShadow: '0 12px 16px -4px rgba(16, 24, 40, 0.18), 0 4px 6px -2px rgba(16, 24, 40, 0.12)', /* --ds-shadow-3 */
    overflow: 'hidden',
    zIndex: 20,
  }),
  // The scroll container paints its own white in react-select's default sheet.
  menuList: (base) => ({ ...base, background: 'var(--ds-surface)' }),
  option: (base, state) => ({
    ...base,
    cursor: 'pointer',
    backgroundColor: state.isSelected
      ? DS_PRIMARY
      : state.isFocused
        ? DS_INFO_BG
        : 'transparent',
    color: state.isSelected ? 'var(--ds-text-on-dark)' : DS_TEXT,
    fontWeight: state.isSelected ? 600 : 500,
  }),
  singleValue: (base) => ({
    ...base,
    color: DS_TEXT,
    fontWeight: 600,
  }),
  placeholder: (base) => ({
    ...base,
    color: DS_FAINT,
  }),
};

export const OnboardingPage: React.FC = () => {
  // Reference lists come from master data (SRS #87); the constants above are the fallback
  // when the request has not completed or fails, so the form never renders empty.
  const [md, setMd] = useState<MasterData | null>(null);
  useEffect(() => { getMasterData().then(setMd).catch(() => {}); }, []);
  const industries = md?.industries ?? INDUSTRIES;
  const organizationTypes = md?.organization_types ?? ORGANIZATION_TYPES;
  const jobRoles = md?.job_roles ?? JOB_ROLES;
  const teamSizes = md?.team_sizes ?? TEAM_SIZES;
  const regulationTypes = md?.regulation_types ?? REGULATION_TYPES;
  const useCases = md?.use_cases ?? USE_CASES;

  // The jurisdictions the corpus actually holds — 150 of them, against the nine
  // that were hardcoded here. Entitlement matches on the name, so a name that
  // is not one of these filters the account down to nothing; offering the
  // corpus's own list is the only way the two cannot disagree.
  const [corpusJurisdictions, setCorpusJurisdictions] = useState<string[] | null>(null);
  useEffect(() => {
    // From the dashboard endpoint rather than search: it is the one that can
    // filter to jurisdictions holding something, and the search version is raw
    // PostgreSQL that no test can execute.
    //
    // with_content: 150 jurisdictions exist and 99 hold anything. The rest are
    // placeholders seeded from the geography list so the map has complete
    // coverage — Alabama, Belize, France. Offered here they are a trap: the
    // choice is accepted, nothing matches it, and nothing says why. Admin
    // screens still list all 150, where the empty ones are the coverage view.
    getJurisdictions({ with_content: true, limit: 1000 })
      .then((rows) => setCorpusJurisdictions(
        Array.from(new Set(rows.map((r) => r.name).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
      ))
      .catch(() => setCorpusJurisdictions(null));
  }, []);
  const jurisdictionOptions = useMemo(
    () => (corpusJurisdictions && corpusJurisdictions.length
      ? corpusJurisdictions
      : JURISDICTIONS_FALLBACK).map((name) => ({ value: name, label: name })),
    [corpusJurisdictions],
  );
  const { user, token, updateUser } = useAuth();

  // How many jurisdictions this plan covers, so the picker can stop at it.
  // Without this the wizard let a Free customer tick eight and the server kept
  // the first one, dropping seven choices in silence — the plan was enforced
  // correctly and the customer was never told.
  const { entitlements } = useBilling();
  const jurisdictionCap: number | null = (() => {
    const raw = entitlements?.jurisdiction_limit;
    return typeof raw === 'number' && raw > 0 ? raw : null;   // null = unlimited or not loaded
  })();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [slideDirection, setSlideDirection] = useState<'next' | 'prev'>('next');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<OnboardingFormData>({
    country: '',
    state: '',
    postal_code: '',
    organization_name: '',
    industry: '',
    organization_type: '',
    job_role: '',
    primary_jurisdictions: [],
    regulation_types: [],
    use_cases: [],
    team_size: '',
    custom_fields: {},
  });

  const [selectedContinent, setSelectedContinent] = useState<string>('');
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('');
  const [postalCodeError, setPostalCodeError] = useState('');
  const [postalAreaInfo, setPostalAreaInfo] = useState<{ status: 'idle' | 'loading' | 'success' | 'error'; area?: string; message?: string }>({ status: 'idle' });
  const [introAnimation, setIntroAnimation] = useState(false);

  // Manual continent mapping based on country codes
  const getContinent = (countryCode: string): string => {
    const continentMap: Record<string, string> = {
      // Africa
      'DZ': 'Africa', 'AO': 'Africa', 'BJ': 'Africa', 'BW': 'Africa', 'BF': 'Africa', 'BI': 'Africa',
      'CM': 'Africa', 'CV': 'Africa', 'CF': 'Africa', 'TD': 'Africa', 'KM': 'Africa', 'CG': 'Africa',
      'CD': 'Africa', 'CI': 'Africa', 'DJ': 'Africa', 'EG': 'Africa', 'GQ': 'Africa', 'ER': 'Africa',
      'ET': 'Africa', 'GA': 'Africa', 'GM': 'Africa', 'GH': 'Africa', 'GN': 'Africa', 'GW': 'Africa',
      'KE': 'Africa', 'LS': 'Africa', 'LR': 'Africa', 'LY': 'Africa', 'MG': 'Africa', 'MW': 'Africa',
      'ML': 'Africa', 'MR': 'Africa', 'MU': 'Africa', 'YT': 'Africa', 'MA': 'Africa', 'MZ': 'Africa',
      'NA': 'Africa', 'NE': 'Africa', 'NG': 'Africa', 'RE': 'Africa', 'RW': 'Africa', 'SH': 'Africa',
      'ST': 'Africa', 'SN': 'Africa', 'SC': 'Africa', 'SL': 'Africa', 'SO': 'Africa', 'ZA': 'Africa',
      'SS': 'Africa', 'SD': 'Africa', 'SZ': 'Africa', 'TZ': 'Africa', 'TG': 'Africa', 'TN': 'Africa',
      'UG': 'Africa', 'ZM': 'Africa', 'ZW': 'Africa',
      // Asia
      'AF': 'Asia', 'AM': 'Asia', 'AZ': 'Asia', 'BH': 'Asia', 'BD': 'Asia', 'BT': 'Asia',
      'BN': 'Asia', 'KH': 'Asia', 'CN': 'Asia', 'GE': 'Asia', 'HK': 'Asia', 'IN': 'Asia',
      'ID': 'Asia', 'IR': 'Asia', 'IQ': 'Asia', 'IL': 'Asia', 'JP': 'Asia', 'JO': 'Asia',
      'KZ': 'Asia', 'KW': 'Asia', 'KG': 'Asia', 'LA': 'Asia', 'LB': 'Asia', 'MO': 'Asia',
      'MY': 'Asia', 'MV': 'Asia', 'MN': 'Asia', 'MM': 'Asia', 'NP': 'Asia', 'KP': 'Asia',
      'OM': 'Asia', 'PK': 'Asia', 'PS': 'Asia', 'PH': 'Asia', 'QA': 'Asia', 'SA': 'Asia',
      'SG': 'Asia', 'KR': 'Asia', 'LK': 'Asia', 'SY': 'Asia', 'TW': 'Asia', 'TJ': 'Asia',
      'TH': 'Asia', 'TL': 'Asia', 'TR': 'Asia', 'TM': 'Asia', 'AE': 'Asia', 'UZ': 'Asia',
      'VN': 'Asia', 'YE': 'Asia',
      // Europe
      'AX': 'Europe', 'AL': 'Europe', 'AD': 'Europe', 'AT': 'Europe', 'BY': 'Europe', 'BE': 'Europe',
      'BA': 'Europe', 'BG': 'Europe', 'HR': 'Europe', 'CY': 'Europe', 'CZ': 'Europe', 'DK': 'Europe',
      'EE': 'Europe', 'FO': 'Europe', 'FI': 'Europe', 'FR': 'Europe', 'DE': 'Europe', 'GI': 'Europe',
      'GR': 'Europe', 'GG': 'Europe', 'HU': 'Europe', 'IS': 'Europe', 'IE': 'Europe', 'IM': 'Europe',
      'IT': 'Europe', 'JE': 'Europe', 'XK': 'Europe', 'LV': 'Europe', 'LI': 'Europe', 'LT': 'Europe',
      'LU': 'Europe', 'MK': 'Europe', 'MT': 'Europe', 'MD': 'Europe', 'MC': 'Europe', 'ME': 'Europe',
      'NL': 'Europe', 'NO': 'Europe', 'PL': 'Europe', 'PT': 'Europe', 'RO': 'Europe', 'RU': 'Europe',
      'SM': 'Europe', 'RS': 'Europe', 'SK': 'Europe', 'SI': 'Europe', 'ES': 'Europe', 'SJ': 'Europe',
      'SE': 'Europe', 'CH': 'Europe', 'UA': 'Europe', 'GB': 'Europe', 'VA': 'Europe',
      // North America
      'AI': 'North America', 'AG': 'North America', 'AW': 'North America', 'BS': 'North America',
      'BB': 'North America', 'BZ': 'North America', 'BM': 'North America', 'BQ': 'North America',
      'CA': 'North America', 'KY': 'North America', 'CR': 'North America', 'CU': 'North America',
      'CW': 'North America', 'DM': 'North America', 'DO': 'North America', 'SV': 'North America',
      'GL': 'North America', 'GD': 'North America', 'GP': 'North America', 'GT': 'North America',
      'HT': 'North America', 'HN': 'North America', 'JM': 'North America', 'MQ': 'North America',
      'MX': 'North America', 'MS': 'North America', 'NI': 'North America', 'PA': 'North America',
      'PM': 'North America', 'PR': 'North America', 'BL': 'North America', 'KN': 'North America',
      'LC': 'North America', 'MF': 'North America', 'VC': 'North America', 'SX': 'North America',
      'TT': 'North America', 'TC': 'North America', 'US': 'North America', 'VG': 'North America',
      'VI': 'North America',
      // South America
      'AR': 'South America', 'BO': 'South America', 'BR': 'South America', 'CL': 'South America',
      'CO': 'South America', 'EC': 'South America', 'FK': 'South America', 'GF': 'South America',
      'GY': 'South America', 'PY': 'South America', 'PE': 'South America', 'SR': 'South America',
      'UY': 'South America', 'VE': 'South America',
      // Oceania
      'AS': 'Oceania', 'AU': 'Oceania', 'CK': 'Oceania', 'FJ': 'Oceania', 'PF': 'Oceania',
      'GU': 'Oceania', 'KI': 'Oceania', 'MH': 'Oceania', 'FM': 'Oceania', 'NR': 'Oceania',
      'NC': 'Oceania', 'NZ': 'Oceania', 'NU': 'Oceania', 'NF': 'Oceania', 'MP': 'Oceania',
      'PW': 'Oceania', 'PG': 'Oceania', 'PN': 'Oceania', 'WS': 'Oceania', 'SB': 'Oceania',
      'TK': 'Oceania', 'TO': 'Oceania', 'TV': 'Oceania', 'VU': 'Oceania', 'WF': 'Oceania',
      // Antarctica
      'AQ': 'Antarctica', 'BV': 'Antarctica', 'TF': 'Antarctica', 'HM': 'Antarctica', 'GS': 'Antarctica',
    };
    return continentMap[countryCode] || 'Other';
  };

  // Build continent options from country data
  const continentOptions = useMemo(() => {
    const continents = new Set<string>();
    Country.getAllCountries().forEach(country => {
      const continent = getContinent(country.isoCode);
      if (continent) continents.add(continent);
    });
    return Array.from(continents).sort().map(continent => ({
      value: continent,
      label: continent,
    }));
  }, []);

  // Build country options filtered by selected continent
  const countryOptions = useMemo((): CountryOption[] => {
    const countries = Country.getAllCountries();
    const filtered = selectedContinent
      ? countries.filter(c => getContinent(c.isoCode) === selectedContinent)
      : countries;
    
    return filtered.map(country => ({
      value: country.name,
      label: country.name,
      isoCode: country.isoCode,
      region: getContinent(country.isoCode),
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [selectedContinent]);

  // Build state options filtered by selected country
  const stateOptions = useMemo((): StateOption[] => {
    if (!selectedCountryCode) return [];
    
    const states = State.getStatesOfCountry(selectedCountryCode);
    return states.map(state => ({
      value: state.name,
      label: state.name,
      stateCode: state.isoCode,
      countryCode: state.countryCode,
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [selectedCountryCode]);

  // Load user's existing data or saved progress from localStorage
  useEffect(() => {
    // First, try to load from user object (for editing existing profile)
    if (user && user.onboarding_completed) {
      const parseJsonField = (field: string | null | undefined): string[] => {
        if (!field) return [];
        try {
          return JSON.parse(field);
        } catch {
          return [];
        }
      };

      setFormData({
        country: user.country || '',
        state: user.state || '',
        postal_code: user.postal_code || '',
        organization_name: user.organization_name || '',
        industry: user.industry || '',
        organization_type: user.organization_type || '',
        job_role: user.job_role || '',
        primary_jurisdictions: parseJsonField(user.primary_jurisdictions),
        regulation_types: parseJsonField(user.regulation_types),
        use_cases: parseJsonField(user.use_cases),
        team_size: user.team_size || '',
        custom_fields: {},
      });

      // Set the country code for state dropdown
      if (user.country) {
        const countries = Country.getAllCountries();
        const foundCountry = countries.find(c => c.name === user.country);
        if (foundCountry) {
          setSelectedCountryCode(foundCountry.isoCode);
          setSelectedContinent(getContinent(foundCountry.isoCode));
        }
      }
    } else {
      // If no user data, try localStorage (for incomplete onboarding)
      const saved = localStorage.getItem('onboarding_progress');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setFormData(parsed.formData);
          setCurrentStep(parsed.currentStep);
          if (!parsed.formData.custom_fields) {
            parsed.formData.custom_fields = {};
          }
          setFormData(parsed.formData);
        } catch (e) {
          console.error('Failed to load saved progress');
        }
      }
    }
  }, [user]);

  useEffect(() => {
    setIntroAnimation(false);
    const timer = requestAnimationFrame(() => setIntroAnimation(true));
    return () => cancelAnimationFrame(timer);
  }, [currentStep]);

  // Lookup postal area info using zippopotam.us when postal code & country are available
  useEffect(() => {
    if (!formData.postal_code) {
      setPostalAreaInfo({ status: 'idle' });
      return;
    }

    if (!selectedCountryCode) {
      setPostalAreaInfo({ status: 'error', message: 'Select a country first so we can verify this postal code.' });
      return;
    }

    if (formData.postal_code.length < 3) {
      setPostalAreaInfo({ status: 'idle' });
      return;
    }

    const controller = new AbortController();
    setPostalAreaInfo({ status: 'loading', message: 'Looking up area…' });

    const fetchArea = async () => {
      try {
        if (selectedCountryCode === 'IN') {
          const response = await fetch(`https://api.postalpincode.in/pincode/${encodeURIComponent(formData.postal_code)}`, {
            signal: controller.signal,
          });
          const data = await response.json();
          const entry = Array.isArray(data) ? data[0] : null;
          if (!entry || entry.Status !== 'Success' || !entry.PostOffice?.length) {
            throw new Error('No matching area for this PIN code');
          }
          const office = entry.PostOffice[0];
          const area = `${office.Name || ''}${office.District ? ', ' + office.District : ''}${office.State ? ', ' + office.State : ''}`.trim();
          setPostalAreaInfo({
            status: 'success',
            area: area || office.Division || 'Area details found',
            message: office.Circle ? `${office.Circle} Circle` : entry.Message || 'PIN verified',
          });
          return;
        }

        const response = await fetch(`https://api.zippopotam.us/${selectedCountryCode.toLowerCase()}/${encodeURIComponent(formData.postal_code)}`, {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error('Area not found for this postal code');
        }
        const data = await response.json();
        const place = Array.isArray(data.places) ? data.places[0] : null;
        if (!place) {
          throw new Error('Area details unavailable');
        }
        const area = `${place['place name'] || ''}${place['place name'] && place.state ? ', ' : ''}${place.state || ''}`.trim();
        setPostalAreaInfo({
          status: 'success',
          area: area || undefined,
          message: data.country || 'Area info found',
        });
      } catch (err) {
        if (controller.signal.aborted) return;
        setPostalAreaInfo({ status: 'error', message: err instanceof Error ? err.message : 'Postal area lookup failed' });
      }
    };

    fetchArea();
    return () => controller.abort();
  }, [formData.postal_code, selectedCountryCode]);

  // Save progress to localStorage
  useEffect(() => {
    localStorage.setItem('onboarding_progress', JSON.stringify({ formData, currentStep }));
  }, [formData, currentStep]);

  const updateField = (field: keyof OnboardingFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateCustomField = (key: string, value: string) => {
    setFormData(prev => {
      const custom_fields = { ...prev.custom_fields };
      if (!value.trim()) {
        delete custom_fields[key];
      } else {
        custom_fields[key] = value;
      }
      return { ...prev, custom_fields };
    });
  };

  const getCustomField = (key: string) => formData.custom_fields[key] || '';

  const toggleArrayField = (
    field: 'primary_jurisdictions' | 'regulation_types' | 'use_cases',
    value: string,
    customFieldKey?: string
  ) => {
    setFormData(prev => {
      const exists = prev[field].includes(value);
      let custom_fields = prev.custom_fields;
      if (customFieldKey && value === 'Other' && exists) {
        custom_fields = { ...prev.custom_fields };
        delete custom_fields[customFieldKey];
      }
      return {
        ...prev,
        [field]: exists
          ? prev[field].filter(v => v !== value)
          : [...prev[field], value],
        custom_fields,
      };
    });
  };

  const handlePostalCodeChange = (value: string) => {
    updateField('postal_code', value);
    setPostalCodeError('');

    if (value && selectedCountryCode) {
      try {
        const validation = postalCodes.validate(selectedCountryCode, value);
        if (!validation) {
          setPostalCodeError('Invalid postal code format for selected country');
        }
      } catch {
        console.log('Postal code validation not available for this country');
      }
    }
  };

  const questions: QuestionConfig[] = [
    {
      id: 'continent',
      title: 'Choose your continent',
      subtitle: 'Start by selecting the region you primarily operate in.',
      icon: 'map',
      validate: () => Boolean(selectedContinent),
      errorMessage: 'Select a continent to continue.',
      renderBody: () => (
        <div>
          <p className={styles.label}>Continent</p>
          <div className={styles.continentGrid}>
            {continentOptions.map(continent => {
              const active = selectedContinent === continent.value;
              const icon = CONTINENT_ICONS[continent.label] || CONTINENT_ICONS.Other;
              return (
                <button
                  type="button"
                  key={continent.value}
                  className={`${styles.continentCard} ${active ? styles.continentCardSelected : ''}`}
                  onClick={() => setSelectedContinent(continent.value)}
                >
                  <span className={styles.continentIcon} aria-hidden="true">{icon}</span>
                  <span className={styles.continentName}>{continent.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ),
    },
    {
      id: 'country',
      title: 'Where is your Headquarters based?',
      subtitle: 'We’ll tailor updates to this country first.',
      icon: 'globe',
      validate: () => Boolean(formData.country),
      errorMessage: 'Select a country to continue.',
      renderBody: () => (
        <div className={styles.field}>
          <label className={styles.label}>Country</label>
          <Select
            value={countryOptions.find(option => option.value === formData.country) || null}
            onChange={(option) => {
              if (!option) return;
              updateField('country', option.value);
              updateField('state', '');
              setSelectedCountryCode((option as CountryOption).isoCode);
            }}
            options={countryOptions}
            styles={selectStyles}
            placeholder="Search and select country..."
          />
          <HelperPanel
            icon={<Globe size={18} aria-hidden />}
            title="Why we ask for your HQ"
            bullets={[
              'Unlock regional dashboards and summaries.',
              'Highlight regulatory alerts specific to your market.',
              'Pre-load templates that match your jurisdiction.'
            ]}
          />
        </div>
      ),
    },
    {
      id: 'state',
      title: 'What state or province are you in?',
      subtitle: 'Some countries skip this step if regions are not tracked.',
      icon: 'pin',
      validate: () => stateOptions.length === 0 || Boolean(formData.state),
      errorMessage: 'Select a state/province to continue.',
      renderBody: () => (
        <div className={styles.field}>
          <label className={styles.label}>State / Province</label>
          <select
            className={styles.select}
            value={formData.state}
            disabled={!selectedCountryCode || stateOptions.length === 0}
            onChange={(e) => updateField('state', e.target.value)}
          >
            <option value="">
              {selectedCountryCode
                ? stateOptions.length ? 'Select state...' : 'States not required for this country'
                : 'Select a country first'}
            </option>
            {stateOptions.map(state => (
              <option key={state.value} value={state.value}>{state.label}</option>
            ))}
          </select>
          {stateOptions.length > 0 && (
            <HelperPanel
              icon={<MapPin size={18} aria-hidden />}
              title="Precision matters"
              bullets={[
                'Surface state-level obligations and notices.',
                'Route alerts to colleagues based on jurisdiction.',
                'Improve accuracy of postal-code validation next step.'
              ]}
            />
          )}
        </div>
      ),
    },
    {
      id: 'postal',
      title: 'What is your postal or ZIP code?',
      subtitle: 'We’ll use this for localized alerts.',
      icon: 'mail',
      validate: () => Boolean(formData.postal_code) && !postalCodeError,
      errorMessage: postalCodeError || 'Enter a valid postal/ZIP code.',
      renderBody: () => (
        <div className={styles.field}>
          <label className={styles.label}>Postal / ZIP code</label>
          <input
            className={styles.input}
            type="text"
            value={formData.postal_code}
            onChange={(e) => handlePostalCodeChange(e.target.value)}
            placeholder="Enter postal code..."
          />
          {postalCodeError && <span className={styles.fieldError}>{postalCodeError}</span>}
          {postalAreaInfo.status === 'loading' && (
            <p className={styles.postalLookupInfo}>{postalAreaInfo.message}</p>
          )}
          {postalAreaInfo.status === 'error' && postalAreaInfo.message && (
            <p className={styles.postalLookupError}>{postalAreaInfo.message}</p>
          )}
          {postalAreaInfo.status === 'success' && (
            <HelperPanel
              icon={<Mailbox size={18} aria-hidden />}
              title={postalAreaInfo.area || 'Area details found'}
              bullets={[postalAreaInfo.message || 'Verified postal code', 'Localized alerts will use this location.']}
            />
          )}
        </div>
      ),
    },
    {
      id: 'organization_type',
      title: 'What type of organization are you?',
      subtitle: 'This tunes the experience for your structure.',
      icon: 'building',
      validate: () => Boolean(formData.organization_type),
      errorMessage: 'Select an organization type.',
      renderBody: () => (
        <div className={styles.optionGridCompact}>
          {organizationTypes.map(type => {
            const active = formData.organization_type === type;
            const icon = ORGANIZATION_TYPE_ICONS[type] || <Building2 size={20} aria-hidden />;
            return (
              <button
                key={type}
                type="button"
                className={`${styles.optionTile} ${active ? styles.optionTileSelected : ''}`}
                onClick={() => updateField('organization_type', active ? '' : type)}
              >
                <span className={styles.optionTileContent}>
                  <span className={styles.optionTileIcon} aria-hidden="true">{icon}</span>
                  <span>{type}</span>
                </span>
              </button>
            );
          })}
        </div>
      ),
    },
    {
      id: 'organization_name',
      title: 'What’s your organization called?',
      subtitle: 'We use this to label dashboards and alerts.',
      icon: 'building',
      validate: () => Boolean(formData.organization_name),
      errorMessage: 'Add your organization name.',
      renderBody: () => (
        <div className={styles.field}>
          <label className={styles.label}>Organization Name</label>
          <input
            className={styles.input}
            type="text"
            value={formData.organization_name}
            onChange={(e) => updateField('organization_name', e.target.value)}
            placeholder="Policyora HQ"
          />
          <HelperPanel
            icon={<Tag size={18} aria-hidden />}
            title="Where you'll see this"
            bullets={[
              'Labels your dashboards, alerts, and exports.',
              'Appears on shared reports with your stakeholders.',
              'Helps tailor copy in team-wide notifications.',
            ]}
          />
        </div>
      ),
    },
    {
      id: 'industry',
      title: 'Which industry best describes you?',
      subtitle: 'Pick the option closest to your operations.',
      icon: 'briefcase',
      validate: () => Boolean(formData.industry),
      errorMessage: 'Select an industry.',
      renderBody: () => (
        <div className={styles.optionGridCompact}>
          {industries.map(industry => {
            const active = formData.industry === industry;
            const icon = INDUSTRY_ICONS[industry] || <Building2 size={20} aria-hidden />;
            return (
              <button
                key={industry}
                type="button"
                className={`${styles.optionTile} ${active ? styles.optionTileSelected : ''}`}
                onClick={() => {
                  if (industry !== 'Other' && formData.industry === 'Other') {
                    updateCustomField('industry_other', '');
                  }
                  updateField('industry', active ? '' : industry);
                  if (industry === 'Other' && active) {
                    updateCustomField('industry_other', '');
                  }
                }}
              >
                <span className={styles.optionTileContent}>
                  <span className={styles.optionTileIcon} aria-hidden="true">{icon}</span>
                  <span>{industry}</span>
                </span>
              </button>
            );
          })}
          {formData.industry === 'Other' && (
            <input
              className={styles.optionInlineInput}
              type="text"
              value={getCustomField('industry_other')}
              onChange={(e) => updateCustomField('industry_other', e.target.value)}
              placeholder="Describe your industry"
            />
          )}
        </div>
      ),
    },
    {
      id: 'job_role',
      title: 'What’s your role?',
      subtitle: 'Helps us surface workflows you care about.',
      icon: 'team',
      validate: () => Boolean(formData.job_role),
      errorMessage: 'Select a role.',
      renderBody: () => (
        <div className={styles.optionGridCompact}>
          {jobRoles.map(role => {
            const active = formData.job_role === role;
            const icon = JOB_ROLE_ICONS[role] || <User size={20} aria-hidden />;
            return (
              <button
                key={role}
                type="button"
                className={`${styles.optionTile} ${active ? styles.optionTileSelected : ''}`}
                onClick={() => {
                  if (role !== 'Other' && formData.job_role === 'Other') {
                    updateCustomField('job_role_other', '');
                  }
                  updateField('job_role', active ? '' : role);
                  if (role === 'Other' && active) {
                    updateCustomField('job_role_other', '');
                  }
                }}
              >
                <span className={styles.optionTileContent}>
                  <span className={styles.optionTileIcon} aria-hidden="true">{icon}</span>
                  <span>{role}</span>
                </span>
              </button>
            );
          })}
          {formData.job_role === 'Other' && (
            <input
              className={styles.optionInlineInput}
              type="text"
              value={getCustomField('job_role_other')}
              onChange={(e) => updateCustomField('job_role_other', e.target.value)}
              placeholder="Describe your role"
            />
          )}
        </div>
      ),
    },
    {
      id: 'team_size',
      title: 'How big is your compliance team?',
      subtitle: 'We’ll right-size collaboration features.',
      icon: 'team',
      validate: () => Boolean(formData.team_size),
      errorMessage: 'Select a team size.',
      renderBody: () => (
        <div className={styles.optionGridCompact}>
          {teamSizes.map(size => {
            const active = formData.team_size === size;
            const icon = TEAM_SIZE_ICONS[size] || <Users size={20} aria-hidden />;
            return (
              <button
                key={size}
                type="button"
                className={`${styles.optionTile} ${active ? styles.optionTileSelected : ''}`}
                onClick={() => updateField('team_size', active ? '' : size)}
              >
                <span className={styles.optionTileContent}>
                  <span className={styles.optionTileIcon} aria-hidden="true">{icon}</span>
                  <span>{size}</span>
                </span>
              </button>
            );
          })}
        </div>
      ),
    },
    {
      id: 'primary_jurisdictions',
      title: 'Which jurisdictions matter most?',
      subtitle: jurisdictionCap
        ? `Search and pick the places you need to follow. Your plan covers ${jurisdictionCap}.`
        : 'Search and pick every jurisdiction you need to follow. Your plan covers as many as you like.',
      icon: 'globe',
      validate: () => formData.primary_jurisdictions.length > 0,
      errorMessage: 'Select at least one jurisdiction.',
      renderBody: () => {
        const chosen = formData.primary_jurisdictions.length;
        const full = jurisdictionCap !== null && chosen >= jurisdictionCap;
        return (
          <div className={styles.field}>
            <label className={styles.label} htmlFor="primary-jurisdictions">Jurisdictions</label>
            {/* A searchable multi-select rather than a grid of tiles: the corpus
                holds 150 jurisdictions and the tiles offered nine. */}
            <Select
              inputId="primary-jurisdictions"
              isMulti
              options={jurisdictionOptions}
              value={formData.primary_jurisdictions.map((name) => ({ value: name, label: name }))}
              onChange={(picked) => {
                const names = (picked as readonly SelectOption[] | null)?.map((o) => o.value) ?? [];
                // Belt as well as braces: the menu is closed at the limit, but a
                // paste or a keyboard select must not get past it either.
                updateField('primary_jurisdictions',
                  jurisdictionCap === null ? names : names.slice(0, jurisdictionCap));
              }}
              styles={multiSelectStyles}
              placeholder="Start typing — United States, European Union, Texas…"
              closeMenuOnSelect={false}
              isOptionDisabled={() => full}
              noOptionsMessage={() => (full
                ? `That is all ${jurisdictionCap} your plan covers — remove one to choose another`
                : 'No jurisdiction of that name is tracked yet')}
            />
            <p className={styles.helperText} data-testid="jurisdiction-allowance">
              {jurisdictionCap !== null
                ? <>
                    <strong>{chosen} of {jurisdictionCap}</strong> chosen.{' '}
                    {full
                      ? <>That is everything your plan covers. Remove one to swap it, or{' '}
                          <a href="/pricing">move to a larger plan</a> to follow more.</>
                      : <>You can pick {jurisdictionCap - chosen} more.</>}
                  </>
                : <>{chosen} chosen — your plan covers as many as you like.</>}
              {corpusJurisdictions ? ` ${corpusJurisdictions.length} jurisdictions are tracked.` : ''}
            </p>
          </div>
        );
      },
    },
    {
      id: 'regulation_types',
      title: 'Which regulation themes do you track?',
      subtitle: 'Choose every category your team covers.',
      icon: 'checkbox',
      validate: () => formData.regulation_types.length > 0,
      errorMessage: 'Select at least one regulation type.',
      renderBody: () => (
        <div className={styles.optionGrid}>
          {regulationTypes.map(type => {
            const active = formData.regulation_types.includes(type);
            const icon = REGULATION_TYPE_ICONS[type] || <FileText size={20} aria-hidden />;
            return (
              <button
                key={type}
                type="button"
                className={`${styles.optionTile} ${active ? styles.optionTileSelected : ''}`}
                onClick={() => toggleArrayField('regulation_types', type, type === 'Other' ? 'regulation_types_other' : undefined)}
              >
                <span className={styles.optionTileContent}>
                  <span className={styles.optionTileIcon} aria-hidden="true">{icon}</span>
                  <span>{type}</span>
                </span>
              </button>
            );
          })}
          {formData.regulation_types.includes('Other') && (
            <input
              className={styles.optionInlineInput}
              type="text"
              value={getCustomField('regulation_types_other')}
              onChange={(e) => updateCustomField('regulation_types_other', e.target.value)}
              placeholder="List the regulation theme"
            />
          )}
        </div>
      ),
    },
    {
      id: 'use_cases',
      title: 'How will you use Policyora?',
      subtitle: 'Optional, but it helps tailor your workspace.',
      icon: 'spark',
      optional: true,
      validate: () => true,
      renderBody: () => (
        <div className={styles.optionGrid}>
          {useCases.map(useCase => {
            const active = formData.use_cases.includes(useCase);
            const icon = USE_CASE_ICONS[useCase] || <Lightbulb size={20} aria-hidden />;
            return (
              <button
                key={useCase}
                type="button"
                className={`${styles.optionTile} ${active ? styles.optionTileSelected : ''}`}
                onClick={() => toggleArrayField('use_cases', useCase, useCase === 'Other' ? 'use_cases_other' : undefined)}
              >
                <span className={styles.optionTileContent}>
                  <span className={styles.optionTileIcon} aria-hidden="true">{icon}</span>
                  <span>{useCase}</span>
                </span>
              </button>
            );
          })}
          {formData.use_cases.includes('Other') && (
            <input
              className={styles.optionInlineInput}
              type="text"
              value={getCustomField('use_cases_other')}
              onChange={(e) => updateCustomField('use_cases_other', e.target.value)}
              placeholder="Describe your use case"
            />
          )}
        </div>
      ),
    },
  ];

  const totalQuestions = questions.length;
  const currentQuestion = questions[currentStep - 1];
  const completionPercent = Math.round((currentStep / totalQuestions) * 100);

  const handleNext = () => {
    const activeQuestion = questions[currentStep - 1];
    if (activeQuestion && !activeQuestion.validate()) {
      setError(activeQuestion.errorMessage || 'Please fill in all required fields');
      return;
    }
    setError(null);
    setSlideDirection('next');
    setCurrentStep(prev => Math.min(prev + 1, totalQuestions));
  };

  const handleBack = () => {
    setError(null);
    setSlideDirection('prev');
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSkip = () => {
    const activeQuestion = questions[currentStep - 1];
    if (!activeQuestion?.optional) {
      setError('This question is mandatory');
      return;
    }
    handleNext();
  };

  const handleSubmit = async () => {
    const incomplete = questions.find(question => !question.optional && !question.validate());
    if (incomplete) {
      setError(incomplete.errorMessage || 'Please complete all required questions');
      const targetIndex = questions.findIndex(q => q.id === incomplete.id);
      if (targetIndex >= 0) {
        setSlideDirection(targetIndex + 1 > currentStep ? 'next' : 'prev');
        setCurrentStep(targetIndex + 1);
      }
      return;
    }

    if (!token) {
      setError('Authentication required');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const updated = await completeOnboarding(token, formData);
      updateUser(updated);
      localStorage.removeItem('onboarding_progress');
      navigate('/billing');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete onboarding');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkipOnboarding = async () => {
    try {
      if (token) {
        const updated = await completeOnboarding(token, {});
        updateUser(updated);
      }
    } catch { /* ignore — ProtectedRoute will redirect if needed */ }
    localStorage.removeItem('onboarding_progress');
    navigate('/billing');
  };

  return (
    <div className={styles.learningRoot}>
      <div className={styles.flowContainer}>
        <header className={styles.flowHeader}>
          <div>
            <Eyebrow tone="default" dot>POLICYORA ONBOARDING</Eyebrow>
            <p className={styles.flowSubtitle} style={{ marginTop: '0.5rem' }}>
              Question {currentStep} of {totalQuestions}
            </p>
          </div>
          <div className={styles.flowHeaderActions}>
            <button
              type="button"
              className={styles.buttonText}
              onClick={handleSkipOnboarding}
              style={{ fontSize: '0.8rem', opacity: 0.7 }}
            >
              Skip onboarding
            </button>
            <div className={styles.progressBadge} aria-label={`Onboarding ${completionPercent}% complete`}>
              <svg viewBox="0 0 64 64">
                <circle className={styles.progressBadgeTrack} cx="32" cy="32" r="28" />
                <circle
                  className={styles.progressBadgeIndicator}
                  cx="32"
                  cy="32"
                  r="28"
                  strokeDasharray={2 * Math.PI * 28}
                  strokeDashoffset={(1 - completionPercent / 100) * 2 * Math.PI * 28}
                />
              </svg>
              <span className={styles.progressBadgeValue}>{completionPercent}%</span>
            </div>
          </div>
        </header>

        {error && (
          <ErrorBanner
            variant="inline"
            align="center"
            message={error}
            details="Please try again after checking your network connection."
          />
        )}

        <div className={styles.cardViewport}>
          <div key={currentStep} className={`${styles.cardSlide} ${styles[`cardSlide-${slideDirection}`]}`}>
            {currentQuestion && (
              <QuestionCard
                title={currentQuestion.title}
                subtitle={currentQuestion.subtitle}
                icon={currentQuestion.icon}
                introActive={introAnimation}
              >
                {currentQuestion.renderBody()}
                {currentQuestion.optional && <p className={styles.optionalHint}>Optional</p>}
              </QuestionCard>
            )}
          </div>
        </div>

        <div className={styles.actions}>
          <div className={styles.actionsLeft}>
            {currentStep > 1 && (
              <button className={styles.buttonSecondary} onClick={handleBack} disabled={submitting}>
                ← Back
              </button>
            )}
          </div>
          <div className={styles.actionsRight}>
            {currentQuestion?.optional && currentStep < totalQuestions && (
              <button className={styles.buttonText} onClick={handleSkip} disabled={submitting}>
                Skip for now
              </button>
            )}
            {currentStep < totalQuestions ? (
              <button className={styles.buttonPrimary} onClick={handleNext} disabled={submitting}>
                Continue
              </button>
            ) : (
              <button className={styles.buttonPrimary} onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Completing…' : 'Complete setup'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
